import { BrandSuggestionResponse } from '../../onboarding/service/brand-fetch/brand-fetch.service';

export type ValueFirstStep =
  | 'path'
  | 'website'
  | 'manual'
  | 'found'
  | 'email'
  | 'code'
  | 'account'
  | 'welcome';

export type ValueFirstPath = 'brand' | 'agency' | 'creator';

export type CampaignCurrency = 'GBP' | 'USD' | 'EUR';

export type DisplayCurrency = 'GBP' | 'USD';

export type ValueFirstAuthMode = 'email' | 'google';

export interface ValueFirstSocial {
  type: string;
  url: string;
}

export interface ValueFirstBrand {
  name: string;
  domain: string;
  logoUrl?: string;
  bannerUrl?: string;
  description?: string;
  industry: string;
  countryCode?: string;
  socials: ValueFirstSocial[];
}

export interface ValueFirstDraft {
  step: ValueFirstStep;
  path: ValueFirstPath | null;
  url: string;
  brand: ValueFirstBrand | null;
  currency: CampaignCurrency | null;
  email: string;
  goals: string[];
  authMode: ValueFirstAuthMode;
  firstName: string;
  lastName: string;
}

export const VALUE_FIRST_STORAGE_KEY = 'shout-value-first-onboarding';
export const VALUE_FIRST_VARIANT = 'value_first';

export const VALUE_FIRST_INDUSTRIES = [
  'Retail & E-commerce',
  'Food & Drink',
  'Beauty & Personal Care',
  'Fashion & Apparel',
  'Tech & Apps',
  'Travel & Hospitality',
  'Health & Fitness',
  'Finance',
  'Other',
] as const;

export const VALUE_FIRST_GOALS = [
  'Brand Awareness',
  'Product Sales',
  'App Installs',
  'Lead Generation',
  'Subscription Signups',
  'Event Registrations',
  'Table Bookings',
  'Donations',
] as const;

export const VALUE_FIRST_CURRENCIES: CampaignCurrency[] = ['GBP', 'USD', 'EUR'];

const EUR_COUNTRY_CODES = new Set([
  'AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
]);

const EUR_TLDS = [
  '.at', '.be', '.hr', '.cy', '.ee', '.fi', '.fr', '.de', '.gr', '.ie', '.it', '.lv', '.lt', '.lu', '.mt', '.nl', '.pt', '.sk', '.si', '.es', '.eu',
];

export interface CurrencyDetection {
  currency: CampaignCurrency;
  detected: boolean;
}

export const BRAND_TRIAL_LINE: Record<DisplayCurrency, string> = {
  GBP: '£199/month (inc. VAT) after your trial. Cancel anytime.',
  USD: '$269/month after your trial. Cancel anytime.',
};

export const BRAND_PATH_PRICE: Record<DisplayCurrency, string> = {
  GBP: '14-day free trial · then £199/mo',
  USD: '14-day free trial · then $269/mo',
};

export const AGENCY_PATH_PRICE: Record<DisplayCurrency, string> = {
  GBP: '14-day free trial · then £449/mo',
  USD: '14-day free trial · then $599/mo',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emptyDraft(): ValueFirstDraft {
  return {
    step: 'path',
    path: null,
    url: '',
    brand: null,
    currency: null,
    email: '',
    goals: ['Brand Awareness'],
    authMode: 'email',
    firstName: '',
    lastName: '',
  };
}

export function normalizeDomain(raw: string): string {
  let value = raw.trim().toLowerCase();
  value = value.replace(/^https?:\/\//, '');
  const slash = value.indexOf('/');
  if (slash >= 0) {
    value = value.substring(0, slash);
  }
  const query = value.indexOf('?');
  if (query >= 0) {
    value = value.substring(0, query);
  }
  return value.replace(/^www\./, '');
}

/** Pounds unless the brand's country, or a country domain, says euros or dollars. */
export function detectCampaignCurrency(domain: string, countryCode?: string | null): CurrencyDetection {
  const country = (countryCode ?? '').trim().toUpperCase();
  if (country === 'GB' || country === 'UK') {
    return { currency: 'GBP', detected: true };
  }
  if (country === 'US') {
    return { currency: 'USD', detected: true };
  }
  if (EUR_COUNTRY_CODES.has(country)) {
    return { currency: 'EUR', detected: true };
  }

  const host = domain.trim().toLowerCase();
  if (host.endsWith('.uk')) {
    return { currency: 'GBP', detected: true };
  }
  if (host.endsWith('.us')) {
    return { currency: 'USD', detected: true };
  }
  if (EUR_TLDS.some((tld) => host.endsWith(tld))) {
    return { currency: 'EUR', detected: true };
  }
  return { currency: 'GBP', detected: false };
}

export function detectCurrency(domain: string): CampaignCurrency {
  return detectCampaignCurrency(domain).currency;
}

export function isValidWorkEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

export function progressIndex(step: ValueFirstStep): 1 | 2 | 3 | null {
  switch (step) {
    case 'website':
    case 'manual':
    case 'found':
      return 1;
    case 'email':
      return 2;
    case 'code':
    case 'account':
      return 3;
    default:
      return null;
  }
}

export function previousStep(step: ValueFirstStep, authMode: ValueFirstAuthMode): ValueFirstStep | null {
  switch (step) {
    case 'website':
      return 'path';
    case 'manual':
    case 'found':
      return 'website';
    case 'email':
      return 'found';
    case 'code':
      return 'email';
    case 'account':
      return authMode === 'google' ? 'email' : 'code';
    case 'welcome':
      return 'email';
    default:
      return null;
  }
}

export function accountFieldError(input: {
  firstName: string;
  lastName: string;
  password: string;
  requirePassword: boolean;
}): string {
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  if (firstName.length < 2) {
    return 'First name must be at least 2 characters long';
  }
  if (firstName.length > 20) {
    return 'First name must be less than 20 characters long';
  }
  if (lastName.length < 2) {
    return 'Last name must be at least 2 characters long';
  }
  if (lastName.length > 20) {
    return 'Last name must be less than 20 characters long';
  }
  if (!input.requirePassword) {
    return '';
  }
  if (input.password.length < 8) {
    return 'Password must be at least 8 characters long';
  }
  if (input.password.length > 20) {
    return 'Password must be less than 20 characters long';
  }
  return '';
}

export function splitPersonName(displayName: string | null | undefined): { firstName: string; lastName: string } {
  const parts = (displayName ?? '').trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function brandInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

function firstImageSrc(formats: unknown): string | undefined {
  if (!Array.isArray(formats)) {
    return undefined;
  }
  for (const format of formats) {
    if (!format || typeof format !== 'object') {
      continue;
    }
    const src = (format as { src?: unknown }).src;
    if (typeof src === 'string' && src.length > 0) {
      return src;
    }
  }
  return undefined;
}

function logoFromBrandfetch(raw: Record<string, unknown>): string | undefined {
  const logos = raw['logos'];
  if (!Array.isArray(logos)) {
    return undefined;
  }
  const preferred = logos.find((logo) => {
    if (!logo || typeof logo !== 'object') {
      return false;
    }
    const type = (logo as { type?: unknown }).type;
    return type === 'icon' || type === 'logo';
  });
  const chosen = preferred ?? logos[0];
  if (!chosen || typeof chosen !== 'object') {
    return undefined;
  }
  return firstImageSrc((chosen as { formats?: unknown }).formats);
}

function bannerFromBrandfetch(raw: Record<string, unknown>): string | undefined {
  const images = raw['images'];
  if (!Array.isArray(images)) {
    return undefined;
  }
  const banner = images.find((image) => {
    if (!image || typeof image !== 'object') {
      return false;
    }
    return (image as { type?: unknown }).type === 'banner';
  });
  const chosen = banner ?? images[0];
  if (!chosen || typeof chosen !== 'object') {
    return undefined;
  }
  return firstImageSrc((chosen as { formats?: unknown }).formats);
}

export function brandFromSuggestion(suggestion: BrandSuggestionResponse): ValueFirstBrand {
  return {
    name: suggestion.name,
    domain: normalizeDomain(suggestion.domain),
    logoUrl: suggestion.icon || undefined,
    industry: '',
    description: '',
    socials: [],
  };
}

export function brandFromBrandfetch(raw: unknown, fallback: ValueFirstBrand): ValueFirstBrand {
  if (!raw || typeof raw !== 'object') {
    return fallback;
  }
  const record = raw as Record<string, unknown>;
  const name = typeof record['name'] === 'string' && record['name'].trim() ? record['name'].trim() : fallback.name;
  const domain = typeof record['domain'] === 'string' && record['domain'].trim()
    ? normalizeDomain(record['domain'])
    : fallback.domain;
  const description = typeof record['description'] === 'string' ? record['description'].trim() : '';
  const longDescription = typeof record['longDescription'] === 'string' ? record['longDescription'].trim() : '';
  const about = [description, longDescription].filter((part) => part.length > 0).join('\n\n');

  let industry = fallback.industry;
  let countryCode = fallback.countryCode;
  const company = record['company'];
  if (company && typeof company === 'object') {
    const industries = (company as { industries?: unknown }).industries;
    if (Array.isArray(industries)) {
      const first = industries.find((item) => item && typeof item === 'object' && typeof (item as { name?: unknown }).name === 'string');
      if (first && typeof first === 'object') {
        industry = String((first as { name: string }).name);
      }
    }
    const location = (company as { location?: unknown }).location;
    if (location && typeof location === 'object') {
      const code = (location as { countryCode?: unknown }).countryCode;
      if (typeof code === 'string' && /^[a-z]{2}$/i.test(code.trim())) {
        countryCode = code.trim().toUpperCase();
      }
    }
  }

  const socials: ValueFirstSocial[] = [];
  const links = record['links'];
  if (Array.isArray(links)) {
    for (const link of links) {
      if (!link || typeof link !== 'object') {
        continue;
      }
      const url = (link as { url?: unknown }).url;
      if (typeof url !== 'string' || url.length === 0) {
        continue;
      }
      const typeValue = (link as { name?: unknown; type?: unknown }).name ?? (link as { type?: unknown }).type;
      socials.push({
        type: typeof typeValue === 'string' && typeValue.length > 0 ? typeValue : 'Link',
        url,
      });
    }
  }

  return {
    name,
    domain,
    logoUrl: logoFromBrandfetch(record) ?? fallback.logoUrl,
    bannerUrl: bannerFromBrandfetch(record),
    description: about,
    industry,
    countryCode,
    socials,
  };
}

export function readDraft(storage: Storage | undefined): ValueFirstDraft | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(VALUE_FIRST_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ValueFirstDraft>;
    const base = emptyDraft();
    return {
      ...base,
      ...parsed,
      goals: Array.isArray(parsed.goals) && parsed.goals.length > 0 ? parsed.goals.filter((goal) => typeof goal === 'string') : base.goals,
      brand: parsed.brand ?? null,
      authMode: parsed.authMode === 'google' ? 'google' : 'email',
    };
  } catch {
    return null;
  }
}

/** Code and account steps need in-memory secrets that a refresh cannot restore. */
export function stepAfterRefresh(draft: ValueFirstDraft): ValueFirstStep {
  if (draft.step === 'account') {
    return draft.authMode === 'google' ? 'email' : 'code';
  }
  return draft.step;
}
